import { useLazyQuery, useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ADD_USERS_TO_TEAM from "@graphql/mutations/AddUsersToTeam";
import CREATE_TEAM from "@graphql/mutations/CreateTeam";
import { INVITE_USERS_TO_TEAM } from "@graphql/mutations/InviteUsersToTeam";
import GET_EXISTING_USERS from "@graphql/queries/GetExistingUsers";
import GET_TEAM from "@graphql/queries/GetTeam";
import { isHandledEmailFlowError } from "@src/utils/emailFlowErrors";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import {
  getBillingIncrementText,
  areThereAnyFailedUsers,
  mapExistingUsersResponse,
  validateUsers,
  buildUsersInput,
} from "./utils";
import { DSModal } from "../../design-system/organisms";
import AddTeamInformation from "../ModalScreens/AddTeamInformation";
import AddUsers from "../ModalScreens/AddUsers";
import ConfirmNewUsers from "../ModalScreens/ConfirmNewUsers";
import NewUsersReceipt from "../ModalScreens/NewUsersReceipt";
import {
  mapTeamData,
  nonBillableRoles,
} from "../TeamManagement/utils";

const TeamModal = ({
  isOpen,
  onClose,
  orgUuid,
  orgAllowsDirectMemberAdd = false,
  orgIsManualBilling = true,
  hasBillingSetup = false,
  team = {
    uuid: "",
    users: [],
    orgUuid: "",
    name: "",
  },
  isCreatingTeam = false,
  refetch = () => {},
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [teamName, setTeamName] = useState("");
  const [teamLocation, setTeamLocation] = useState("");
  const [newUserEmails, setNewUserEmails] = useState([]);
  const [newUsersState, setNewUsersState] = useState([]);
  const [existingUsersState, setExistingUsersState] = useState([]);
  const [deactivatedUsersState, setDeactivatedUsersState] = useState(
    [],
  );
  const [receiptData, setReceiptData] = useState({});
  const [createdTeam, setCreatedTeam] = useState(null);
  const navigate = useNavigate();

  const stepOffset = isCreatingTeam ? 0 : 1;
  const activeTeam = createdTeam ?? team;

  const [addUsersToTeam, { loading: addUsersLoading }] =
    useMutation(ADD_USERS_TO_TEAM);

  const [getTeam] = useLazyQuery(GET_TEAM, {
    fetchPolicy: "no-cache",
  });

  const [createTeam, { loading: teamCreationLoading }] =
    useMutation(CREATE_TEAM);

  const [getExistingUsers, { loading: existingUsersLoading }] =
    useLazyQuery(GET_EXISTING_USERS, {
      fetchPolicy: "network-only",
    });

  const [inviteUsersToTeam, { loading: inviteUsersLoading }] =
    useMutation(INVITE_USERS_TO_TEAM);

  const billingIncrementText = useMemo(() => {
    const newUsersEmailArray = newUsersState.filter(
      (user) =>
        !nonBillableRoles.includes(user.roleId) &&
        user.accountType == "unlimited",
    );
    const existingUsersEmailArray = existingUsersState.filter(
      (user) =>
        !nonBillableRoles.includes(user.roleId) &&
        user.accountType == "unlimited",
    );
    return getBillingIncrementText(
      newUsersEmailArray,
      existingUsersEmailArray,
    );
  }, [newUsersState, existingUsersState]);

  const updateStep = (nextStep) => {
    setErrorMessage("");
    setCurrentStep(nextStep);
  };

  const updateTeamName = (nextTeamName) => {
    setErrorMessage("");
    setTeamName(nextTeamName);
  };

  const updateTeamLocation = (nextTeamLocation) => {
    setErrorMessage("");
    setTeamLocation(nextTeamLocation);
  };

  const resetState = () => {
    setCurrentStep(0);
    setErrorMessage("");
    setTeamName("");
    setTeamLocation("");
    setNewUserEmails([]);
    setNewUsersState([]);
    setExistingUsersState([]);
    setDeactivatedUsersState([]);
    setCreatedTeam(null);
    refetch();
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      setErrorMessage("Please enter a team name to proceed.");
      return;
    }

    try {
      const { data } = await createTeam({
        variables: {
          teamName,
          location: teamLocation,
          createdAt: new Date().toISOString(),
        },
      });

      try {
        const teamResult = await getTeam({
          variables: { uuid: data.createTeam.teamUuid },
        });
        setCreatedTeam(mapTeamData(teamResult.data));
      } catch {
        alert("error", "Failed to refresh team data.");
      }

      mixpanel.track("Team created", {
        teamName,
        teamLocation,
        orgUuid: orgUuid,
      });
      updateStep(1);
    } catch (error) {
      console.error("Create team error:", error);
      alert("error", "An error occurred while creating the team.");
      mixpanel.track("Error creating team", {
        error: error.message,
        teamName,
        teamLocation,
        orgUuid: orgUuid,
      });
    }
  };

  const handleAddUsers = async () => {
    if (newUserEmails.length > 0) {
      try {
        const { data } = await getExistingUsers({
          variables: { emails: newUserEmails },
        });
        const {
          newUsersState: mappedNewUsers,
          existingUsersState: mappedExistingUsers,
          deactivatedUsers,
        } = mapExistingUsersResponse(data?.existingUsers);
        setNewUsersState(mappedNewUsers || []);
        setExistingUsersState(mappedExistingUsers || []);
        setDeactivatedUsersState(deactivatedUsers || []);
        updateStep(currentStep + 1);
      } catch (error) {
        console.error("Get existing users error:", error);
        alert(
          "error",
          "An error occurred while fetching existing users.",
        );
      }
    } else {
      alert("error", "Please add at least one user to continue.");
    }
  };

  const handleConfirmNewUsers = async () => {
    const { ok, error } = validateUsers({
      newUsers: newUsersState,
      existingUsers: existingUsersState,
      isOrg: orgAllowsDirectMemberAdd,
    });
    if (!ok) {
      setErrorMessage(error);
      return;
    }

    const usersInput = buildUsersInput({
      newUsers: newUsersState,
      existingUsers: existingUsersState,
      teamUuid: activeTeam.uuid,
      createdAt: moment().format(),
      isOrg: orgAllowsDirectMemberAdd,
    });

    if (orgAllowsDirectMemberAdd) {
      try {
        const { data } = await addUsersToTeam({
          variables: { addUsersToTeamInput: usersInput },
        });
        if (
          areThereAnyFailedUsers(
            data?.addUsersToTeam?.provisionedUsers,
          )
        ) {
          alert(
            "error",
            "You have custom billing requirements. Please contact support.",
          );
          return;
        }
        setReceiptData(data?.addUsersToTeam?.provisionedUsers);
        updateStep(currentStep + 1);
        mixpanel.track("Users added to team", {
          teamUuid: activeTeam?.uuid,
          orgUuid: orgUuid,
          addedUsers: data?.addUsersToTeam?.provisionedUsers,
        });
      } catch (error) {
        console.error("Add users error:", error);
        if (!isHandledEmailFlowError(error)) {
          alert("error", "An error occurred while adding users.");
        }
        mixpanel.track("Error adding users to team", {
          error: error.message,
          teamUuid: activeTeam?.uuid,
          orgUuid: orgUuid,
        });
      }
    } else {
      try {
        const { data } = await inviteUsersToTeam({
          variables: { inviteUsersToTeamInput: usersInput },
        });
        const provisionedUsers = [
          ...data.inviteUsersToTeam.provisionedUsers.newUsers.map(
            (user) => ({
              ...user,
              newUser: true,
            }),
          ),
          ...data.inviteUsersToTeam.provisionedUsers.existing.map(
            (user) => ({
              ...user,
              newUser: false,
            }),
          ),
        ];

        provisionedUsers.forEach((user) => {
          mixpanel.track("User invited to team", {
            teamUuid: activeTeam.uuid,
            orgUuid: orgUuid,
            email: user.email,
            inviteUuid: user.inviteUuid,
            newUser: user.newUser,
          });
        });
        setReceiptData(data?.inviteUsersToTeam?.provisionedUsers);
        updateStep(currentStep + 1);
      } catch (error) {
        console.error("Invite users error:", error);
        if (!isHandledEmailFlowError(error)) {
          alert("error", "An error occurred while inviting users.");
        }
        mixpanel.track("Error inviting users to team", {
          error: error.message,
          teamUuid: activeTeam.uuid,
          orgUuid: orgUuid,
        });
      }
    }
  };

  const closeModalAndResetState = () => {
    resetState();
    refetch();
    onClose();
  };

  const steps = [
    {
      component: (
        <AddTeamInformation
          teamName={teamName}
          setTeamName={updateTeamName}
          setTeamLocation={updateTeamLocation}
          error={errorMessage}
        />
      ),
      title: "Create a Team",
      confirmText: "Next",
      confirmAction: handleCreateTeam,
      cancelText: "Cancel",
      cancelDataTestId: "cancel-button",
      cancelAction: closeModalAndResetState,
    },
    {
      component: (
        <AddUsers
          newUserEmails={newUserEmails}
          setNewUserEmails={setNewUserEmails}
          team={activeTeam}
          isNewTeam={isCreatingTeam}
        />
      ),
      title: `${orgAllowsDirectMemberAdd ? "Add" : "Invite"} Members to ${
        teamName || activeTeam?.name
      }`,
      confirmText: "Next",
      confirmAction: handleAddUsers,
      cancelText: isCreatingTeam ? "I'll Invite Later" : "Cancel",
      cancelDataTestId: isCreatingTeam
        ? "invite-later-button"
        : "cancel-button",
      cancelAction: () => {
        closeModalAndResetState();
        if (isCreatingTeam) {
          // refresh to get new team
          window.location.reload();
        }
      },
    },
    {
      component: (
        <ConfirmNewUsers
          newUsers={newUsersState}
          existingUsers={existingUsersState}
          deactivatedUsers={deactivatedUsersState}
          setUsersState={({ newUsers, existingUsers }) => {
            setNewUsersState(newUsers);
            setExistingUsersState(existingUsers);
            setNewUserEmails(
              [...newUsers, ...existingUsers].map(
                (user) => user.email,
              ),
            );
          }}
          orgAllowsDirectMemberAdd={orgAllowsDirectMemberAdd}
          hasBillingSetup={hasBillingSetup}
          orgIsManualBilling={orgIsManualBilling}
          error={errorMessage}
          billingIncrementText={billingIncrementText}
        />
      ),
      title: `Confirm ${
        orgAllowsDirectMemberAdd ? "Additions" : "Invitations"
      } to ${teamName || activeTeam?.name}`,
      confirmText: "Next",
      confirmAction: handleConfirmNewUsers,
      cancelText: "Previous",
      cancelDataTestId: "cancel-button",
      cancelAction: () => updateStep(1 - stepOffset),
      disableConfirm:
        !newUsersState.length && !existingUsersState.length,
    },
    {
      component: (
        <NewUsersReceipt
          receiptData={receiptData}
          orgAllowsDirectMemberAdd={orgAllowsDirectMemberAdd}
        />
      ),
      title: `Team Members ${orgAllowsDirectMemberAdd ? "Added" : "Invited"}`,
      confirmText: "Finish",
      hideCancel: true,
      confirmAction: () => {
        closeModalAndResetState();
        navigate(`/teams/${activeTeam.uuid}`);
      },
    },
  ];

  const currentStepData = steps[currentStep + stepOffset];
  const isAnythingLoading =
    teamCreationLoading ||
    existingUsersLoading ||
    addUsersLoading ||
    inviteUsersLoading;

  const modalActions = (() => {
    if (isAnythingLoading) return [];
    const actions = [];
    if (!currentStepData.hideCancel) {
      actions.push({
        label: currentStepData.cancelText || "Cancel",
        variant: "cancel",
        onClick: currentStepData.cancelAction,
        dataTestId: currentStepData.cancelDataTestId,
      });
    }
    actions.push({
      label: currentStepData.confirmText || "Confirm",
      variant: "primary",
      onClick: currentStepData.confirmAction,
      disabled: currentStepData.disableConfirm,
      dataTestId: "confirm-button",
    });
    return actions;
  })();

  return (
    <DSModal
      isOpen={isOpen}
      onClose={closeModalAndResetState}
      title={currentStepData.title}
      dataTestId="team-modal"
      width="w-[70vw] max-w-[900px]"
      actions={modalActions}
    >
      {isAnythingLoading ? (
        <div
          className="flex justify-center"
          data-testid="loading-spinner"
        >
          <FontAwesomeIcon
            icon={faSpinner}
            className="animate-spin text-secondary text-4xl"
          />
        </div>
      ) : (
        <>
          {currentStepData?.component}
          {errorMessage &&
            (newUsersState?.length > 0 ||
              existingUsersState?.length > 0) && (
              <div className="text-danger mt-sm">{errorMessage}</div>
            )}
        </>
      )}
    </DSModal>
  );
};

export default TeamModal;
